import React, { useEffect, useState, useRef } from "react";
import "./Overview.scss";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import { adminRequest } from "../../utils/requestMethods";
import { BASE_URL } from "../../utils/config";
import { useNavigate } from "react-router-dom";
import { FaArrowLeftLong } from "react-icons/fa6";
import Loader from "../../components/Loader/Loader";

const Overview = () => {
  const [courseList, setCourseList] = useState([]);
  const [formData, setFormData] = useState({ course: { id: "" } });
  const [data, setData] = useState(null);
  const tableRef = useRef();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const handleBackClick = () => {
    navigate(-1);
  };

  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const res = await adminRequest.get(`${BASE_URL}/course/getNameAndId`);
        setCourseList(res.data.data || []);
      } catch (err) {
        console.error("Error fetching courses:", err);
      }
    };
    fetchCourses();
  }, []);

  const handleCourseChange = (e) => {
    const selectedId = e.target.value;
    const selectedCourse = courseList.find((c) => String(c.id) === selectedId);
    setFormData((prev) => ({
      ...prev,
      course: selectedCourse || { id: "" },
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.course.id) {
      alert("Please select a course.");
      return;
    }

    try {
      setLoading(true);
      const res = await adminRequest.get(
        `${BASE_URL}/student/status/course?courseId=${formData.course.id}`,
      );
      setData(res.data.data || {});
    } catch (error) {
      console.error("Error fetching student status:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="overview">
        <Loader />
      </div>
    );
  }

  return (
    <div className="overview">
      <div className="top">
        <span className="backIcon" onClick={handleBackClick}>
          <FaArrowLeftLong />
        </span>
        <span className="headerTitle">Student Overview</span>
      </div>
      <form
        className="form-row"
        onSubmit={handleSubmit}
        style={{ display: "flex", gap: "20px", alignItems: "flex-end" }}
      >
        <div className="form-group">
          <label>Course</label>
          <select
            value={formData.course.id ? String(formData.course.id) : ""}
            onChange={handleCourseChange}
            required
          >
            <option value="">Select Course</option>
            {courseList.map((c) => (
              <option key={c.id} value={String(c.id)}>
                {c.short_name}
              </option>
            ))}
          </select>
        </div>
        <button type="submit" style={{ height: "38px", padding: "0 20px" }}>
          Search
        </button>
      </form>
      {data && (
        <Box mt={2}>
          <div ref={tableRef} className="print-area">
            <TableContainer component={Paper}>
              <Table size="small" aria-label="student-demographics">
                <TableHead>
                  <TableRow>
                    <TableCell rowSpan={2} sx={{ border: "1px solid #ccc" }}>
                      <b>SEMESTER</b>
                    </TableCell>
                    <TableCell rowSpan={2} sx={{ border: "1px solid #ccc" }}>
                      <b>SECTION</b>
                    </TableCell>

                    <TableCell
                      colSpan={2}
                      align="center"
                      sx={{ border: "1px solid #ccc" }}
                    >
                      <b>MALE</b> (Total: {data?.MALE_STUDENT || 0})
                    </TableCell>
                    <TableCell
                      colSpan={2}
                      align="center"
                      sx={{ border: "1px solid #ccc" }}
                    >
                      <b>FEMALE</b> (Total: {data?.FEMALE_STUDENT || 0})
                    </TableCell>
                    <TableCell
                      colSpan={2}
                      align="center"
                      sx={{ border: "1px solid #ccc" }}
                    >
                      <b>OTHERS</b>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell align="center" sx={{ border: "1px solid #ccc" }}>
                      <b>ACTIVE</b>
                    </TableCell>
                    <TableCell align="center" sx={{ border: "1px solid #ccc" }}>
                      <b>PENDING</b>
                    </TableCell>
                    <TableCell align="center" sx={{ border: "1px solid #ccc" }}>
                      <b>ACTIVE</b>
                    </TableCell>
                    <TableCell align="center" sx={{ border: "1px solid #ccc" }}>
                      <b>PENDING</b>
                    </TableCell>
                    <TableCell align="center" sx={{ border: "1px solid #ccc" }}>
                      <b>ACTIVE</b>
                    </TableCell>
                    <TableCell align="center" sx={{ border: "1px solid #ccc" }}>
                      <b>PENDING</b>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Object.keys(data || {})
                    .filter(
                      (key) =>
                        !["TOTAL", "MALE_STUDENT", "FEMALE_STUDENT"].includes(
                          key,
                        ),
                    )
                    .map((semester) => {
                      const semesterData = data[semester];
                      if (!semesterData) return null;

                      const sectionKeys = Object.keys(semesterData).filter(
                        (k) =>
                          !["TOTAL", "MALE_STUDENT", "FEMALE_STUDENT"].includes(
                            k,
                          ),
                      );

                      return sectionKeys.map((sectionKey, idx) => {
                        const section = semesterData[sectionKey];

                        return (
                          <TableRow key={`${semester}-${sectionKey}`}>
                            {idx === 0 && (
                              <TableCell
                                rowSpan={sectionKeys.length}
                                sx={{ border: "1px solid #ccc" }}
                              >
                                <b>{semester}</b>
                                <br />
                                (Total: {semesterData.TOTAL})
                              </TableCell>
                            )}
                            <TableCell sx={{ border: "1px solid #ccc" }}>
                              <b>{sectionKey}</b>
                              <br />
                              (Total: {section.TOTAL})
                            </TableCell>
                            <TableCell
                              align="center"
                              sx={{ border: "1px solid #ccc" }}
                            >
                              {section?.MALE?.ACTIVE || 0}
                            </TableCell>
                            <TableCell
                              align="center"
                              sx={{ border: "1px solid #ccc" }}
                            >
                              {section?.MALE?.PENDING || 0}
                            </TableCell>
                            <TableCell
                              align="center"
                              sx={{ border: "1px solid #ccc" }}
                            >
                              {section?.FEMALE?.ACTIVE || 0}
                            </TableCell>
                            <TableCell
                              align="center"
                              sx={{ border: "1px solid #ccc" }}
                            >
                              {section?.FEMALE?.PENDING || 0}
                            </TableCell>
                            <TableCell
                              align="center"
                              sx={{ border: "1px solid #ccc" }}
                            >
                              {section?.OTHERS?.ACTIVE || 0}
                            </TableCell>
                            <TableCell
                              align="center"
                              sx={{ border: "1px solid #ccc" }}
                            >
                              {section?.OTHERS?.PENDING || 0}
                            </TableCell>
                          </TableRow>
                        );
                      });
                    })}
                </TableBody>
              </Table>
            </TableContainer>
            <div className="gap">
              Total: {data?.TOTAL || 0}, Male: {data?.MALE_STUDENT || 0},
              Female: {data?.FEMALE_STUDENT || 0}
            </div>
          </div>
        </Box>
      )}
    </div>
  );
};

export default Overview;
